import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { EmptyState } from "@/components/EmptyState";
import { RecordFormModal } from "@/components/RecordFormModal";
import { NewClientForm } from "@/components/NewClientForm";
import { DashboardAiIntakeBar } from "@/components/DashboardAiIntakeBar";
import { TaskQuickAdd } from "@/components/tasks/TaskQuickAdd";
import { SectionPanel } from "@/components/Section";
import { HealthMarker } from "@/components/HealthMarker";
import { StageGlyph } from "@/components/StageGlyph";
import { DensityProvider } from "@/contexts/DensityContext";
import { toast } from "sonner";
import {
  AlertTriangle, MessageSquareWarning, ClipboardCheck, CalendarClock, Repeat, ArrowRight,
  Square, X, Clapperboard, Users2, MessageSquare,
} from "lucide-react";

// Dashboard 1B redesign (2026-02) — full rebuild per the founder's NJM-Dashboard-1B-Standalone
// mockup. "Needs Attention" and "Active Projects" are the only two headline sections; My
// Tasks/Client Portal Activity/This Month/NJM Build are a slim right-hand rail. No money on
// this page at all — Receivables/at-risk-client-value live at /accounting (Finance.jsx), never
// duplicated here. The PM/Founder toggle is a FOUNDER-ONLY self-preview (backend already only
// sends `can_preview_pm_view: true` to founder/partner/finance) — it can only narrow what a
// founder sees on their own screen, never reveal anything to an actual PM/coordinator account.
const CATEGORY_ICONS = {
  "Client Feedback": MessageSquareWarning, "Approval Waiting": ClipboardCheck,
  "Overdue Milestone": CalendarClock, "Assignment Change": Repeat, "Project At Risk": AlertTriangle,
};

const PRIORITY_DOT = { High: "bg-priority-high", Medium: "bg-priority-medium", Low: "bg-priority-low" };

const QUOTES = [
  "Cinema is a matter of what's in the frame and what's out.", "In visual storytelling, the filmmaker's obligation is to be as clear as possible.",
  "A film is never really good unless the camera is an eye in the head of a poet.", "The way you cut a film is the way you think.",
  "Every film is a work in progress until the day it's released.", "A good story is the foundation of every great production.",
  "Filmmaking is a chance to live many lifetimes.", "The camera is a mirror. It shows exactly what you show it.",
  "Great work happens when brief, budget, and belief line up.", "Post-production is where the real film gets discovered.",
  "The best light is the light you didn't have to fight for.", "Every shoot day is a small negotiation with time.",
  "Editing is the invisible art that makes everything else visible.", "A brief well understood is half the film already made.",
  "The client's story matters more than our style.", "Momentum on a project is a team's most valuable currency.",
  "A deadline missed once is a lesson; missed twice is a pattern.", "Deliverables ship on trust, not just on time.",
  "The best crews make hard days look effortless.", "Nothing replaces being on set when it matters.",
];

function dayOfYear() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  return Math.floor((now - start) / 86400000);
}

function timeAgo(iso) {
  if (!iso) return null;
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function greetingWord() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function callSheetDate() {
  return new Date().toLocaleDateString("en-US", { weekday: "long", day: "numeric", month: "long" }).toUpperCase();
}

// approval_service stores `proposed_by` as a raw email (kernel/approvals.py) — presented here as
// a calmer display name, never a fabricated one (just reformatting the same real string).
function prettyLabel(label) {
  if (!label || !label.includes("@")) return label;
  const local = label.split("@")[0];
  return local.replace(/[._]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function DashboardSkeleton() {
  return (
    <div className="p-8 sm:p-10 space-y-6" data-testid="dashboard-skeleton">
      <div className="h-48 rounded-xl bg-surface2 animate-pulse" />
      <div className="h-12 rounded-lg bg-surface2 animate-pulse" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 h-80 rounded-lg bg-surface2 animate-pulse" />
        <div className="h-80 rounded-lg bg-surface2 animate-pulse" />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [myTasksOverdue, setMyTasksOverdue] = useState([]);
  const [myTasksToday, setMyTasksToday] = useState([]);
  const [myTasksUpcoming, setMyTasksUpcoming] = useState([]);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [showAllAttention, setShowAllAttention] = useState(false);
  // Item 8 (2026-10) fix — the key must include the user's own id: a shared browser profile
  // (e.g. two founders/partners signing in on the same machine, or the SAME person switching
  // between a founder and partner test account) used to leak one user's PM-view preview choice
  // into the next user's session, since `localStorage` is per-browser-profile, not per-login.
  const previewAsPmKey = user?.id ? `dashboard_preview_as_pm:${user.id}` : "dashboard_preview_as_pm";
  const [previewAsPm, setPreviewAsPmState] = useState(() => localStorage.getItem(previewAsPmKey) === "1");
  // Wave C fix 2D (2026-10) — PM View/Founder View choice used to be plain component state,
  // reset to Founder View every time Dashboard.jsx remounted (e.g. navigating to another menu
  // item and back) — persisted in localStorage so it survives navigation within the session.
  const setPreviewAsPm = (value) => {
    setPreviewAsPmState(value);
    localStorage.setItem(previewAsPmKey, value ? "1" : "0");
  };
  const [showNewProject, setShowNewProject] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [showTaskAdd, setShowTaskAdd] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = () => {
    setLoadError(false);
    return api.get("/v1/dashboard/").then((r) => {
      setData(r.data);
      setMyTasksOverdue(r.data.my_tasks_overdue || []);
      setMyTasksToday(r.data.my_tasks_today || []);
      setMyTasksUpcoming(r.data.my_tasks_upcoming || []);
      setNeedsAttention(r.data.needs_attention || []);
      if (r.data.duplicate_project_names?.length) {
        // eslint-disable-next-line no-console
        console.warn("Dashboard: duplicate active-project names found in the underlying data — flag to the founder:", r.data.duplicate_project_names);
      }
    }).catch(() => setLoadError(true));
    // Perf fix (2026-10, Cursor audit item 9 / group 0C): this used to have no .catch — a
    // failed request left `data` null forever, permanently stuck on the skeleton below with
    // no way for the user to know something went wrong or to try again.
  };

  useEffect(() => { load(); }, []);

  if (!data && loadError) {
    return (
      <div className="p-8 sm:p-10 space-y-3" data-testid="dashboard-error">
        <p className="text-sm text-[#9BA1A4]">Couldn't load your dashboard.</p>
        <button onClick={load} data-testid="dashboard-retry-btn"
          className="inline-flex items-center rounded-[7px] border border-[#E5E6EC] bg-white px-3.5 py-2 text-[12.5px] font-semibold text-[#495053] hover:border-[#FFC817]">
          Retry
        </button>
      </div>
    );
  }
  if (!data) return <DashboardSkeleton />;

  const myTasksTotal = myTasksOverdue.length + myTasksToday.length + myTasksUpcoming.length;
  const quote = QUOTES[dayOfYear() % QUOTES.length];
  // A founder previewing "PM VIEW" only ever narrows the SAME payload already sent to them —
  // Approval Waiting rows sourced from partner-budget negotiations (founder/partner-only by
  // gate) are the one category a real PM would never see, so hide those specifically.
  const visibleAttention = previewAsPm ? needsAttention.filter((n) => !n.key.startsWith("partner_decision:")) : needsAttention;
  const shownAttention = showAllAttention ? visibleAttention : visibleAttention.slice(0, 5);

  const completeTask = async (taskId, setter) => {
    // Perf fix (2026-10, Cursor audit item 9 / group 0C): the optimistic removal used to have
    // no revert-on-failure — a failed PATCH silently left the task permanently gone from the
    // list even though it was never actually marked Done server-side.
    let removed, removedIndex;
    setter((prev) => {
      removedIndex = prev.findIndex((t) => t.id === taskId);
      removed = prev[removedIndex];
      return prev.filter((t) => t.id !== taskId);
    });
    try {
      await api.patch(`/v1/tasks/${taskId}`, { status: "Done" });
      // Wave C fix 2D (2026-10) — same Undo pattern Tasks.jsx/ProjectTasksSection.jsx already
      // use: completing from the Dashboard's own My Tasks list had no undo at all.
      toast.success("Task completed", { action: { label: "Undo", onClick: () => {
        setter((prev) => { const next = [...prev]; next.splice(Math.min(removedIndex, next.length), 0, removed); return next; });
        api.patch(`/v1/tasks/${taskId}`, { status: "Open" }).catch(() => {});
      } } });
    } catch {
      toast.error("Couldn't complete that task — it's back on your list");
      if (removed) setter((prev) => { const next = [...prev]; next.splice(Math.min(removedIndex, next.length), 0, removed); return next; });
    }
  };

  const dismissAttention = async (item) => {
    setNeedsAttention((prev) => prev.filter((n) => n.key !== item.key));
    try { await api.post("/v1/dashboard/dismiss", { key: item.key }); } catch {}
  };

  const quickAddTask = async (title) => {
    try {
      await api.post("/v1/tasks/", { title });
      toast.success("Task added");
      load();
    } catch {
      toast.error("Couldn't add task");
    }
  };

  return (
    <DensityProvider density="standard" data-testid="dashboard-page" className="p-8 sm:p-10 space-y-6">
      {/* Masthead — "call sheet" editorial header, always dark regardless of light-app theme */}
      <div className="rounded-xl bg-ink-deep px-6 sm:px-8 py-7 space-y-5" data-testid="dashboard-masthead">
        <div className="flex items-center justify-between gap-4">
          <div className="text-[11px] uppercase tracking-[0.18em] text-paper/50" data-testid="dashboard-call-sheet-date">
            {callSheetDate()} · Call Sheet
          </div>
          {data.can_preview_pm_view && (
            <div className="flex items-center rounded border border-white/15 p-0.5 shrink-0" data-testid="dashboard-view-toggle">
              <button data-testid="dashboard-view-toggle-pm" onClick={() => setPreviewAsPm(true)}
                className={`rounded-sm px-3 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${previewAsPm ? "bg-brand text-ink" : "text-paper/60 hover:text-paper"}`}>
                PM View
              </button>
              <button data-testid="dashboard-view-toggle-founder" onClick={() => setPreviewAsPm(false)}
                className={`rounded-sm px-3 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${!previewAsPm ? "bg-brand text-ink" : "text-paper/60 hover:text-paper"}`}>
                Founder View
              </button>
            </div>
          )}
        </div>
        <div>
          <h1 className="font-editorial text-3xl sm:text-4xl text-paper" data-testid="dashboard-greeting">
            {greetingWord()}, {data.greeting_name || "there"}
          </h1>
          <p className="text-sm text-paper/60 mt-2 italic max-w-lg" data-testid="dashboard-quote">&ldquo;{quote}&rdquo;</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="quick-action-new-project" onClick={() => setShowNewProject(true)}
            className="inline-flex h-8 items-center rounded border border-white/20 px-3 text-[13px] font-medium text-paper hover:bg-white/10 transition-colors">
            + New Project
          </button>
          <button data-testid="quick-action-new-client" onClick={() => setShowNewClient(true)}
            className="inline-flex h-8 items-center rounded border border-white/20 px-3 text-[13px] font-medium text-paper hover:bg-white/10 transition-colors">
            + New Client
          </button>
          <button data-testid="quick-action-build-quote" onClick={() => navigate("/commercial")}
            className="inline-flex h-8 items-center rounded border border-white/20 px-3 text-[13px] font-medium text-paper hover:bg-white/10 transition-colors">
            Build Quote
          </button>
          <button data-testid="quick-action-create-task" onClick={() => setShowTaskAdd((s) => !s)}
            className="inline-flex h-8 items-center rounded border border-white/20 px-3 text-[13px] font-medium text-paper hover:bg-white/10 transition-colors">
            Create Task
          </button>
        </div>
        {showTaskAdd && (
          <div className="rounded-md border border-white/15 bg-white/5 overflow-hidden" data-testid="quick-task-add-panel">
            <TaskQuickAdd onCreate={quickAddTask} placeholder="Task title — press Enter to add…" testId="dashboard-quick-task-input" />
          </div>
        )}
      </div>

      <DashboardAiIntakeBar />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {visibleAttention.length > 0 && (
            <SectionPanel testId="needs-attention-panel"
              title={
                <span className="inline-flex items-center gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-health-attention-dot" />Needs Your Attention
                  <span data-testid="needs-attention-count" className="grid h-[18px] min-w-[18px] place-items-center rounded-full bg-brand px-1.5 text-[11px] font-semibold text-ink">
                    {visibleAttention.length}
                  </span>
                </span>
              }
              action={visibleAttention.length > 5 && (
                <button data-testid="needs-attention-view-all" onClick={() => setShowAllAttention((s) => !s)} className="text-xs font-medium text-gold-text hover:text-ink">
                  {showAllAttention ? "Show less" : `View all ${visibleAttention.length}`}
                </button>
              )}
            >
              <div className="divide-y divide-hairline">
                {shownAttention.map((n, i) => {
                  const Icon = CATEGORY_ICONS[n.category] || AlertTriangle;
                  return (
                    <div key={n.key || i} className="flex items-center gap-1 px-2 group">
                      <Link to={n.link} data-testid={`needs-attention-item-${i}`}
                        className="flex items-center gap-3 flex-1 min-w-0 px-3 py-3 text-sm hover:bg-surface2 transition-colors rounded-md">
                        <Icon className="h-3.5 w-3.5 text-tone-red-dot shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-tone-red-text">{n.category}</div>
                          <div className="text-ink truncate mt-0.5"><span className="font-semibold">{prettyLabel(n.label)}</span> <span className="text-ink-soft">· {n.sublabel}</span></div>
                        </div>
                        <ArrowRight className="h-3 w-3 text-faint shrink-0" />
                      </Link>
                      <button
                        data-testid={`needs-attention-dismiss-${i}`}
                        onClick={() => dismissAttention(n)}
                        title="Dismiss — hides this card only, never touches the record"
                        className="shrink-0 rounded-md p-2 text-faint opacity-0 group-hover:opacity-100 hover:bg-surface2 hover:text-health-attention-text transition-all"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </SectionPanel>
          )}

          <SectionPanel testId="active-projects-panel" title="Active Projects"
            action={<Link to="/projects" data-testid="active-projects-view-all" className="text-xs font-medium text-gold-text hover:text-ink">View all</Link>}
          >
            <div className="divide-y divide-hairline">
              {data.active_projects.length === 0 && <EmptyState title="No active projects" hint="Projects sync from Notion once created there." testId="dashboard-projects-empty" />}
              {data.active_projects.map((p) => (
                <Link key={p.id} to={`/projects/${p.id}`} data-testid={`dashboard-project-row-${p.id}`}
                  className="px-5 py-3.5 flex items-center gap-4 hover:bg-surface2 transition-colors">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink truncate">{p.name}</div>
                    <div className="flex items-center gap-2 text-[12px] text-ink-soft mt-1 truncate">
                      {p.client_name && <span className="truncate">{p.client_name}</span>}
                      {p.client_name && <span className="text-hairline-strong">·</span>}
                      <StageGlyph stage={p.phase} testId={`dashboard-project-stage-${p.id}`} />
                      <span className="text-hairline-strong">·</span>
                      <span className="shrink-0">{p.pm_name ? `PM ${p.pm_name}` : "PM not assigned"}</span>
                    </div>
                  </div>
                  {p.next_label && (
                    <div className="text-right shrink-0 hidden sm:block">
                      <div className="text-[11px] uppercase tracking-wider text-faint">{p.next_label}</div>
                      <div className="text-[11px] text-ink mt-0.5">{p.next_value || "\u2014"}</div>
                    </div>
                  )}
                  <div className="shrink-0">
                    <HealthMarker health={p.health} testId={`dashboard-project-health-${p.id}`} />
                  </div>
                </Link>
              ))}
            </div>
          </SectionPanel>
        </div>

        <div className="space-y-6">
          {myTasksTotal > 0 && (
            <SectionPanel testId="my-tasks-panel" title="My Tasks"
              action={<Link to="/tasks" data-testid="my-tasks-view-all-link" className="text-xs font-medium text-gold-text hover:text-ink">View all</Link>}
            >
              {[
                { key: "overdue", label: "Overdue", items: myTasksOverdue, setter: setMyTasksOverdue },
                { key: "today", label: "Due Today", items: myTasksToday, setter: setMyTasksToday },
                { key: "upcoming", label: "Upcoming", items: myTasksUpcoming, setter: setMyTasksUpcoming },
              ].filter((g) => g.items.length > 0).map((g) => (
                <div key={g.key}>
                  <div className="px-5 py-1.5 text-[11px] uppercase tracking-wider text-faint bg-surface2" data-testid={`my-tasks-section-${g.key}`}>{g.label} ({g.items.length})</div>
                  <div className="divide-y divide-hairline">
                    {g.items.map((t) => (
                      <div key={t.id} data-testid={`my-task-${t.id}`} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                        <button data-testid={`my-task-complete-${t.id}`} onClick={() => completeTask(t.id, g.setter)} title="Mark done" className="shrink-0 text-faint hover:text-ink">
                          <Square className="h-4 w-4" />
                        </button>
                        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${PRIORITY_DOT[t.priority] || PRIORITY_DOT.Medium}`} title={t.priority} />
                        <div className="min-w-0 flex-1">
                          <div className="text-ink font-medium truncate">{t.title}</div>
                          {(t.project_name || t.due_date) && (
                            <div className={`text-[11px] mt-0.5 ${g.key === "overdue" ? "text-tone-red-text" : "text-faint"}`}>{[t.project_name, t.due_date].filter(Boolean).join(" · ")}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </SectionPanel>
          )}

          {data.client_portal_activity.length > 0 && (
            <SectionPanel testId="client-portal-activity-panel"
              title={<span className="inline-flex items-center gap-2"><MessageSquare className="h-3.5 w-3.5 text-faint" />Client Portal Activity</span>}
            >
              <div className="divide-y divide-hairline">
                {data.client_portal_activity.map((c, i) => (
                  <div key={i} data-testid={`client-portal-activity-${i}`} className="px-5 py-3 text-sm">
                    <div className="text-ink"><span className="font-medium">{c.client_name}</span> {c.text}</div>
                    <div className="text-[11px] text-faint mt-0.5">{timeAgo(c.when)}</div>
                  </div>
                ))}
              </div>
            </SectionPanel>
          )}

          <SectionPanel testId="this-month-panel" title="This Month">
            <div className="grid grid-cols-2 divide-x divide-y divide-hairline">
              <div className="px-3 py-4 text-center" data-testid="this-month-projects">
                <div className="font-editorial text-xl text-ink">{data.this_month.projects_worked_on}</div>
                <div className="text-[11px] text-faint mt-1 uppercase tracking-wider">Projects Worked On</div>
              </div>
              <div className="px-3 py-4 text-center" data-testid="this-month-done">
                <div className="font-editorial text-xl text-ink">{data.this_month.projects_done}</div>
                <div className="text-[11px] text-faint mt-1 uppercase tracking-wider">Projects Done</div>
              </div>
              <div className="px-3 py-4 text-center" data-testid="this-month-shoots">
                <div className="font-editorial text-xl text-ink">{data.this_month.shoots}</div>
                <div className="text-[11px] text-faint mt-1 uppercase tracking-wider">Shoots</div>
              </div>
              <div className="px-3 py-4 text-center" data-testid="this-month-deliverables">
                <div className="font-editorial text-xl text-ink">{data.this_month.deliverables}</div>
                <div className="text-[11px] text-faint mt-1 uppercase tracking-wider">Deliverables</div>
              </div>
            </div>
          </SectionPanel>

          {data.community_highlights.length > 0 && (
            <SectionPanel testId="njm-build-panel"
              title={<span className="inline-flex items-center gap-2"><Clapperboard className="h-3.5 w-3.5 text-faint" />NJM Build</span>}
              action={<Link to="/community" data-testid="njm-build-view-all" className="text-xs font-medium text-gold-text hover:text-ink">View all</Link>}
            >
              <div className="divide-y divide-hairline">
                {data.community_highlights.map((h, i) => (
                  <div key={i} data-testid={`njm-build-highlight-${i}`} className="px-5 py-3 text-sm text-ink flex items-start gap-2.5">
                    <Users2 className="h-3.5 w-3.5 text-faint shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="truncate">{h.text}</div>
                      <div className="text-[11px] text-faint mt-0.5">{timeAgo(h.when)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </SectionPanel>
          )}
        </div>
      </div>

      <RecordFormModal type="project" mode="create" open={showNewProject} onClose={() => setShowNewProject(false)}
        onCreated={(record) => navigate(`/projects/${record.id}`)} />
      <NewClientForm open={showNewClient} onClose={() => setShowNewClient(false)}
        onCreated={() => { setShowNewClient(false); load(); }} />
    </DensityProvider>
  );
}
